/**
 * "Marque onde houver avarias" — o desenho do caminhão dos checklists de papel.
 *
 * Quatro vistas (as duas laterais do conjunto, frente e traseira). Um toque no
 * desenho marca; cada marca ganha número, e a nota curta ("para-lama amassado")
 * vai com ela para a ocorrência. A posição é gravada relativa ao desenho (0–1),
 * então a marca cai no mesmo lugar no celular e no monitor.
 *
 * "Sem avarias" é uma resposta explícita (`marks: []`): no papel, desenho em
 * branco não distingue "sem avaria" de "não olhei".
 */
import { useEffect, useState } from "react";
import type { MaintenanceDamageMapDto, MaintenanceDamageMarkDto, MaintenanceDamageViewDto } from "@bi/types";

const VISTAS: Array<{ view: MaintenanceDamageViewDto; label: string; viewBox: string; wide: boolean }> = [
  { view: "left", label: "Lateral esquerda", viewBox: "0 0 400 120", wide: true },
  { view: "right", label: "Lateral direita", viewBox: "0 0 400 120", wide: true },
  { view: "front", label: "Frente", viewBox: "0 0 160 140", wide: false },
  { view: "rear", label: "Traseira", viewBox: "0 0 160 140", wide: false },
];

export const DAMAGE_VIEW_LABEL: Record<MaintenanceDamageViewDto, string> = {
  left: "Lateral esquerda",
  right: "Lateral direita",
  front: "Frente",
  rear: "Traseira",
};

const MAX_MARCAS = 30;

interface Props {
  value: MaintenanceDamageMapDto | null;
  readOnly: boolean;
  onChange: (v: MaintenanceDamageMapDto) => void;
}

export function DamageMap({ value, readOnly, onChange }: Props) {
  const marcas = value?.marks ?? [];
  const numero = new Map(marcas.map((m, i) => [m.id, i + 1]));

  const marcar = (view: MaintenanceDamageViewDto, e: React.MouseEvent<SVGSVGElement>) => {
    if (readOnly || marcas.length >= MAX_MARCAS) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = clamp((e.clientX - r.left) / r.width);
    const y = clamp((e.clientY - r.top) / r.height);
    const id = `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
    onChange({ marks: [...marcas, { id, view, x: arred(x), y: arred(y), note: null }] });
  };

  const remover = (id: string) => onChange({ marks: marcas.filter((m) => m.id !== id) });
  const anotar = (id: string, note: string) =>
    onChange({ marks: marcas.map((m) => (m.id === id ? { ...m, note: note.trim() || null } : m)) });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {VISTAS.map((v) => (
          <figure key={v.view} className={`rounded-lg border border-surface-border bg-white p-2 ${v.wide ? "col-span-2" : ""}`}>
            <figcaption className="text-xs text-gray-500 mb-1">{v.label}</figcaption>
            <svg
              viewBox={v.viewBox}
              className={`w-full h-auto text-gray-400 select-none ${readOnly ? "" : "cursor-crosshair touch-manipulation"}`}
              role={readOnly ? "img" : "button"}
              aria-label={readOnly ? v.label : `${v.label}: toque para marcar avaria`}
              onClick={(e) => marcar(v.view, e)}
            >
              <Desenho view={v.view} />
              {marcas.filter((m) => m.view === v.view).map((m) => {
                const [w, h] = v.viewBox.split(" ").slice(2).map(Number) as [number, number];
                return (
                  <g key={m.id} transform={`translate(${m.x * w} ${m.y * h})`} className="pointer-events-none">
                    <circle r={v.wide ? 9 : 8} className="fill-danger-600" />
                    <text textAnchor="middle" dy="0.35em" fontSize={v.wide ? 10 : 9} className="fill-white font-semibold">
                      {numero.get(m.id)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </figure>
        ))}
      </div>

      {marcas.length > 0 ? (
        <ol className="space-y-2">
          {marcas.map((m) => (
            <MarcaLinha key={m.id} marca={m} numero={numero.get(m.id)!} readOnly={readOnly}
              onRemover={() => remover(m.id)} onAnotar={(n) => anotar(m.id, n)} />
          ))}
        </ol>
      ) : value ? (
        <p className="text-sm text-tm-700 bg-tm-50 rounded-lg px-3 py-2">Sem avarias, conferido.</p>
      ) : (
        !readOnly && (
          <button
            className="w-full min-h-[2.75rem] rounded-lg border border-surface-border bg-white text-sm font-medium text-gray-700 hover:bg-surface-muted"
            onClick={() => onChange({ marks: [] })}
          >
            Sem avarias
          </button>
        )
      )}
      {!readOnly && marcas.length >= MAX_MARCAS && (
        <p className="text-xs text-warning-700">Limite de {MAX_MARCAS} marcas. Descreva o resto na observação.</p>
      )}
    </div>
  );
}

function MarcaLinha({
  marca, numero, readOnly, onRemover, onAnotar,
}: { marca: MaintenanceDamageMarkDto; numero: number; readOnly: boolean; onRemover: () => void; onAnotar: (n: string) => void }) {
  const [nota, setNota] = useState(marca.note ?? "");
  useEffect(() => setNota(marca.note ?? ""), [marca.note]);
  return (
    <li className="flex items-center gap-2">
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-danger-600 text-white text-xs font-semibold flex items-center justify-center">
        {numero}
      </span>
      <span className="flex-shrink-0 text-xs text-gray-500 w-24">{DAMAGE_VIEW_LABEL[marca.view]}</span>
      {readOnly ? (
        <span className="text-sm text-gray-800">{marca.note ?? "—"}</span>
      ) : (
        <>
          <input
            className="input flex-1 min-w-0"
            maxLength={200}
            placeholder="O que há aqui? (ex.: amassado)"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            onBlur={() => { if ((marca.note ?? "") !== nota.trim()) onAnotar(nota); }}
          />
          <button className="btn-ghost min-h-[2.5rem] text-danger-600 text-sm" onClick={onRemover} aria-label={`Remover marca ${numero}`}>
            Remover
          </button>
        </>
      )}
    </li>
  );
}

const clamp = (n: number) => Math.min(1, Math.max(0, n));
const arred = (n: number) => Math.round(n * 1000) / 1000;

/** Traço simples do conjunto cavalo + carreta — o bastante para situar a marca. */
function Desenho({ view }: { view: MaintenanceDamageViewDto }) {
  const traco = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinejoin: "round" as const };
  if (view === "left" || view === "right") {
    // Lateral esquerda: frente à esquerda (visto do lado do motorista). A direita espelha.
    return (
      <g transform={view === "right" ? "translate(400 0) scale(-1 1)" : undefined}>
        {/* cabine */}
        <path {...traco} d="M18 88 V46 Q18 30 34 26 L70 20 Q80 19 82 28 V88 Z" />
        <path {...traco} d="M26 50 L34 32 L66 27 V50 Z" />
        <path {...traco} d="M86 88 V60 H118 V88" />
        {/* carreta */}
        <rect {...traco} x="124" y="22" width="264" height="66" rx="3" />
        <path {...traco} d="M18 88 H392" />
        {/* rodas */}
        {[42, 100, 300, 334, 368].map((cx) => (
          <circle key={cx} {...traco} cx={cx} cy="96" r="12" />
        ))}
      </g>
    );
  }
  if (view === "front") {
    return (
      <g>
        <path {...traco} d="M24 110 V34 Q24 14 44 12 H116 Q136 14 136 34 V110 Z" />
        <path {...traco} d="M34 24 H126 V58 H34 Z" />
        <rect {...traco} x="50" y="70" width="60" height="26" rx="2" />
        <rect {...traco} x="30" y="72" width="14" height="10" rx="2" />
        <rect {...traco} x="116" y="72" width="14" height="10" rx="2" />
        <path {...traco} d="M20 110 H140" />
        <rect {...traco} x="28" y="112" width="18" height="22" rx="3" />
        <rect {...traco} x="114" y="112" width="18" height="22" rx="3" />
      </g>
    );
  }
  return (
    <g>
      <rect {...traco} x="18" y="10" width="124" height="100" rx="3" />
      <path {...traco} d="M80 10 V110" />
      <rect {...traco} x="24" y="96" width="12" height="8" rx="1" />
      <rect {...traco} x="124" y="96" width="12" height="8" rx="1" />
      <rect {...traco} x="60" y="112" width="40" height="10" rx="1" />
      <rect {...traco} x="22" y="112" width="30" height="22" rx="3" />
      <rect {...traco} x="108" y="112" width="30" height="22" rx="3" />
    </g>
  );
}
